import React, { useState, useEffect } from "react";
import CommonModal from "../../../../components/modals/commonModel";
import DefaultBadge from "../../../../components/badge/defaultBadge";
import { HiTrendingUp, HiTrendingDown } from "react-icons/hi";
import moment from "moment";
import { getStaffIncrementHistory } from "../../../../../services/workspace/salaryIncrement.service";
import toast from "react-hot-toast";
import CommonTable from "../../../../components/table/table";

const SalaryIncrementHistory = ({ staffId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [incrementHistory, setIncrementHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalData, setTotalData] = useState(0);

  useEffect(() => {
    if (staffId) {
      fetchIncrementHistory();
    }
  }, [staffId, currentPage]);

  const fetchIncrementHistory = () => {
    setLoading(true);
    getStaffIncrementHistory(staffId, { page: currentPage, limit: 10 })
      .then((response) => {
        const data = response?.increment?.data || response?.data || [];
        setIncrementHistory(data);
        setTotalData(response?.increment?.meta?.total || response?.meta?.total || 0);
      })
      .catch((error) => {
        toast.error("Failed to fetch increment history!");
        console.error("Error fetching increment history:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("DD MMM YYYY");
  };

  const TableHeaderData = [
    { label: "Increment Year", key: "increment_year" },
    { label: "Effective Date", key: "effective_date" },
    { label: "Previous CTC", key: "previous_ctc" },
    { label: "New CTC", key: "new_ctc" },
    { label: "Increment Amount", key: "increment_amount" },
    { label: "Increment %", key: "increment_percentage" },
    { label: "Created At", key: "created_at" },
  ];

  const tableData = incrementHistory.map((item) => {
    const incrementAmount = item.new_ctc - item.previous_ctc;
    const incrementPercentage = item.previous_ctc > 0
      ? ((incrementAmount / item.previous_ctc) * 100).toFixed(2)
      : 0;

    return {
      id: { data: item.uuid || item.id, variant: "text" },
      increment_year: {
        data: item.increment_year || (item.effective_date ? moment(item.effective_date).format("YYYY") : "-"),
        variant: "text",
      },
      effective_date: {
        data: formatDate(item.effective_date),
        variant: "text",
      },
      previous_ctc: {
        data: item.previous_ctc,
        variant: "currency",
      },
      new_ctc: {
        data: item.new_ctc,
        variant: "currency",
      },
      increment_amount: {
        data: incrementAmount,
        variant: "currency",
        className: "text-green-600 font-semibold",
      },
      increment_percentage: {
        data: `${incrementPercentage}%`,
        variant: "text",
        className: "text-green-600 font-semibold",
      },
      created_at: {
        data: moment(item.created_at).format("DD MMM YYYY, hh:mm A"),
        variant: "text",
      },
    };
  });

  return (
    <CommonModal
      modalSize="xl"
      headerTitle="Salary Increment History"
      onClose={onClose}
      show={true}
    >
      <div className="p-4">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-textLight">Loading increment history...</p>
          </div>
        ) : incrementHistory.length === 0 ? (
          <div className="text-center py-8">
            <HiTrendingUp className="text-4xl text-textLight mx-auto mb-3" />
            <p className="text-textLight">No increment history found</p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-blue-600 font-medium mb-1">Total Increments</p>
                <p className="text-2xl font-bold text-blue-900">
                  {incrementHistory.length}
                </p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs text-green-600 font-medium mb-1">Latest CTC</p>
                <p className="text-2xl font-bold text-green-900">
                  {formatCurrency(incrementHistory[0]?.new_ctc || 0)}
                </p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-xs text-purple-600 font-medium mb-1">Initial CTC</p>
                <p className="text-2xl font-bold text-purple-900">
                  {formatCurrency(
                    incrementHistory[incrementHistory.length - 1]?.previous_ctc || 0
                  )}
                </p>
              </div>
            </div>

            {/* Increment History Table */}
            <CommonTable
              TableHeaderData={TableHeaderData}
              tabledata={tableData}
              currentPage={currentPage}
              totalData={totalData}
              setCurrentPage={setCurrentPage}
              loading={loading}
            />
          </>
        )}
      </div>
    </CommonModal>
  );
};

export default SalaryIncrementHistory;

